import { onBeforeUnmount, ref, watch, type Ref } from 'vue';
import type { SlideCanvasBox, SlideRenderBox } from '../components/SlideCanvas.types';
import { createLinearRenderer } from '../utils/slideRenderer';

export type SlideCanvasRendererOptions = {
  width: Ref<number>;
  height: Ref<number>;
  background: Ref<string>;
  backgroundImage: Ref<HTMLImageElement | null>;
  stroke: Ref<string>;
  fill: Ref<string>;
  draftStroke: Ref<string>;
  draftFill: Ref<string>;
  selectedStroke: string;
  selectedFill: string;
  renderBoxes: Ref<SlideRenderBox[]>;
  draftBox: Ref<SlideCanvasBox | null>;
  selectedBoxId: Ref<string | null>;
  resolveColor: (label: string) => { stroke: string; fill: string };
};

export function useSlideCanvasRenderer(options: SlideCanvasRendererOptions) {
  const canvasRef = ref<HTMLCanvasElement | null>(null);
  const frameHandle = ref<number | null>(null);
  const renderer = createLinearRenderer();

  function scheduleDraw() {
    if (frameHandle.value) {
      cancelAnimationFrame(frameHandle.value);
    }
    frameHandle.value = requestAnimationFrame(() => {
      frameHandle.value = null;
      draw();
    });
  }

  function syncContext() {
    renderer.sync(canvasRef.value, options.width.value, options.height.value);
    scheduleDraw();
  }

  function draw() {
    renderer.draw({
      background: options.background.value,
      backgroundImage: options.backgroundImage.value,
      renderBoxes: options.renderBoxes.value,
      selectedBoxId: options.selectedBoxId.value,
      selectedStroke: options.selectedStroke,
      selectedFill: options.selectedFill,
      stroke: options.stroke.value,
      fill: options.fill.value,
      draftBox: options.draftBox.value,
      draftStroke: options.draftStroke.value,
      draftFill: options.draftFill.value,
      width: options.width.value,
      height: options.height.value,
      resolveColor: options.resolveColor,
    });
  }

  watch(options.renderBoxes, () => scheduleDraw(), { deep: true });
  watch(options.draftBox, () => scheduleDraw(), { deep: true });
  watch([options.stroke, options.fill, options.background, options.selectedBoxId], () => scheduleDraw());
  watch(options.backgroundImage, () => scheduleDraw());
  watch([options.width, options.height], () => {
    syncContext();
  });

  onBeforeUnmount(() => {
    if (frameHandle.value) {
      cancelAnimationFrame(frameHandle.value);
    }
  });

  return { canvasRef, scheduleDraw, syncContext };
}
